import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import type { AiSpendReportView, GeoSpendView } from '@vello/api-client';
import { AUTO_REFRESH_MS, Button, EmptyState, Grid, KpiGrid, LineChart, Notice, Panel, Row, Stack, StatCard, Text, Tooltip, useAutoRefresh, type StatDelta } from '@vello/web-theme';
import {
  aiStreams,
  buildCostCard,
  costFx,
  mapsStreams,
  microsMoney,
  platformCostsAuthority,
  previousMonthOf,
  type CostAmount,
  type CostCardModel,
  type CostChange,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * D874: the platform costs the Admin shows - the **Mapas** and **IA** cards of the dashboard's "Custos de plataforma" block, over the two spend reads
 * (`GET /v1/config/integrations/geo/spend`, `GET /v1/ai/spend`), and the pieces the "Gastos de mapas" screen reuses (the card body, the money text, the maps read).
 * Money is shown to the cent, approximate (`≈`) when converted at the stored rate; with no rate yet the vendor's currency stays and the card says so.
 */

export type Load<T> = { readonly status: 'loading' } | { readonly status: 'error'; readonly reason: string } | { readonly status: 'ready'; readonly value: T };

/** Money text of amounts in micros: one per currency joined with a plus; `≈` in front when converted. */
export function amountsText(amounts: readonly CostAmount[] | undefined, lang: string, approx: boolean): string {
  if (amounts === undefined || amounts.length === 0) return '—';
  return `${approx ? '≈ ' : ''}${amounts.map((a) => microsMoney(a.micros.toString(), a.currency, lang)).join(' + ')}`;
}

/** The maps spend read, refreshed on the report cadence; `enabled` false makes no call. */
export function useGeoSpend(enabled: boolean): Load<GeoSpendView> {
  const { runtime } = useAdmin();
  const { fail } = useVocab();
  const [state, setState] = useState<Load<GeoSpendView>>({ status: 'loading' });
  const load = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const res = await runtime.integrations.getGeoSpend();
    setState(res.ok ? { status: 'ready', value: res.value } : { status: 'error', reason: fail(res.error) });
  }, [runtime, enabled, fail]);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, runtime]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.report, enabled });
  return state;
}

/** The AI spend of this month and, for the chart and the month total, of the month before (the previous one failing only loses that part). */
export function useAiSpend(enabled: boolean): Load<{ readonly report: AiSpendReportView; readonly previous: AiSpendReportView | undefined }> {
  const { runtime } = useAdmin();
  const { fail } = useVocab();
  const [state, setState] = useState<Load<{ report: AiSpendReportView; previous: AiSpendReportView | undefined }>>({ status: 'loading' });
  const load = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    const res = await runtime.aiSpend.spend();
    if (!res.ok) {
      setState({ status: 'error', reason: fail(res.error) });
      return;
    }
    const before = await runtime.aiSpend.spend(previousMonthOf(res.value.month));
    setState({ status: 'ready', value: { report: res.value, previous: before.ok ? before.value : undefined } });
  }, [runtime, enabled, fail]);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, runtime]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.report, enabled });
  return state;
}

/** The change line as the plain words the owner asked for, with the tone and direction of the StatCard delta. */
function changeDelta(change: CostChange | undefined, t: (id: string, vars?: Record<string, string | number>) => string): StatDelta | undefined {
  if (change === undefined) return undefined;
  const sr = t('pcost.change.sr');
  switch (change.kind) {
    case 'up':
      return { text: t('pcost.change.up', { percent: change.percent }), direction: 'up', tone: 'warning', srLabel: sr, arrow: true };
    case 'down':
      return { text: t('pcost.change.down', { percent: change.percent }), direction: 'down', tone: 'success', srLabel: sr, arrow: true };
    case 'flat':
      return { text: t('pcost.change.flat'), direction: 'flat', srLabel: sr };
    case 'none':
      return { text: t('pcost.change.none'), direction: 'flat' };
  }
}

/** The money line of a chart axis and tooltip: whole currency units. */
const chartMoney = (n: number, currency: string, lang: string): string => new Intl.NumberFormat(lang, { style: 'currency', currency }).format(n);

/** The body of a cost card: the four figures, the exchange note and the chart. Shared by the dashboard card and the maps screen. */
export function CostBody({ model, originalLabel }: { model: CostCardModel; originalLabel: (source: string) => string }): ReactElement {
  const { t, lang } = useT();
  const money = (a: readonly CostAmount[] | undefined): string => amountsText(a, lang, model.approx);
  /** The ends of the range read without the approximation sign: the figure beside them already says it. */
  const plain = (a: readonly CostAmount[] | undefined): string => amountsText(a, lang, false);
  const delta = changeDelta(model.change, t);
  const forecast = model.forecast;
  const originals = model.original;
  const chart = model.chart;
  return (
    <Stack gap={4}>
      <KpiGrid>
        <StatCard label={t('pcost.spent')} value={money(model.spent)} hint={t('pcost.spent.day', { day: model.today, days: model.daysInMonth })} {...(delta !== undefined ? { delta } : {})} />
        <StatCard label={t('pcost.same')} value={money(model.samePeriod)} hint={t('pcost.same.hint', { day: model.today })} />
        <StatCard
          label={t('pcost.forecast')}
          value={forecast === undefined ? t('pcost.forecast.unknown') : money(forecast.mid)}
          {...(forecast !== undefined ? { hint: t('pcost.forecast.range', { low: plain(forecast.low), high: plain(forecast.high) }) } : {})}
        />
        <StatCard label={t('pcost.last')} value={model.lastMonthTotal === undefined ? t('pcost.last.unknown') : money(model.lastMonthTotal)} />
      </KpiGrid>
      {model.shortHistory && <Text as="p" role="caption" tone="secondary">{t('pcost.forecast.short')}</Text>}
      {chart !== undefined && (
        <LineChart
          title={t('pcost.chart.title')}
          labels={chart.labels}
          series={[
            { key: 'thisMonth', label: t('pcost.chart.thisMonth'), values: chart.thisMonth },
            { key: 'forecast', label: t('pcost.chart.forecast'), values: chart.forecast },
            { key: 'lastMonth', label: t('pcost.chart.lastMonth'), values: chart.lastMonth },
          ]}
          area={false}
          smooth={false}
          formatValue={(n) => chartMoney(n, chart.currency, lang)}
          emptyLabel={t('pcost.chart.empty')}
          height={200}
        />
      )}
      {model.approx && model.rateDate !== undefined && (
        <Text as="p" role="caption" tone="tertiary">
          {t('pcost.rate.note', { date: new Date(`${model.rateDate}T12:00:00.000Z`).toLocaleDateString(lang, { timeZone: 'UTC' }) })}
          {model.rateStale ? ` ${t('pcost.rate.stale')}` : ''}
        </Text>
      )}
      {model.missingRate && <Text as="p" role="caption" tone="secondary">{t('pcost.rate.missing')}</Text>}
      {model.approx && originals.length > 0 && (
        <Row gap={2} align="center">
          <Tooltip content={originals.map((o) => t('pcost.original.line', { source: originalLabel(o.source), amount: microsMoney(o.spent.toString(), o.currency, lang) })).join(' · ')}>
            <Button variant="ghost" size="sm" type="button">{t('pcost.original')}</Button>
          </Tooltip>
        </Row>
      )}
    </Stack>
  );
}

interface CardProps {
  readonly title: string;
  readonly subtitle: string;
  readonly state: Load<CostCardModel>;
  readonly what: string;
  readonly emptyText: string;
  readonly detailsLabel: string;
  readonly onDetails: (() => void) | undefined;
  readonly originalLabel: (source: string) => string;
}

function CostCard({ title, subtitle, state, what, emptyText, detailsLabel, onDetails, originalLabel }: CardProps): ReactElement {
  const { t } = useT();
  return (
    <Panel>
      <Stack gap={4}>
        <Row gap={3} align="baseline" justify="between" wrap>
          <Stack gap={1}>
            <h3>{title}</h3>
            <Text as="p" role="caption" tone="secondary">{subtitle}</Text>
          </Stack>
          {onDetails !== undefined && <Button variant="ghost" size="sm" type="button" onClick={onDetails}>{detailsLabel}</Button>}
        </Row>
        {state.status === 'loading' && <Text as="p" role="caption" tone="secondary">{t('ui.loading')}</Text>}
        {state.status === 'error' && <Notice tone="danger">{t('pcost.loadFailed', { what, reason: state.reason })}</Notice>}
        {state.status === 'ready' && (state.value.empty ? <EmptyState title={emptyText} /> : <CostBody model={state.value} originalLabel={originalLabel} />)}
      </Stack>
    </Panel>
  );
}

/** The dashboard's "Custos de plataforma" block: the Mapas and IA cards the operator may read (a card the operator may not read is not drawn, and makes no call). */
export function PlatformCostsBlock(): ReactElement | null {
  const { grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  const { v } = useVocab();
  const { mayViewMaps, mayViewAi } = platformCostsAuthority(grants);
  const geo = useGeoSpend(mayViewMaps);
  const ai = useAiSpend(mayViewAi);

  const mapsCard = useMemo<Load<CostCardModel>>(() => {
    if (geo.status !== 'ready') return geo;
    const streams = mapsStreams(geo.value);
    const aiReport = ai.status === 'ready' ? ai.value.report : undefined;
    return { status: 'ready', value: buildCostCard('maps', streams, costFx(geo.value, aiReport, streams.map((s) => s.currency))) };
  }, [geo, ai]);
  const aiCard = useMemo<Load<CostCardModel>>(() => {
    if (ai.status !== 'ready') return ai;
    const streams = aiStreams(ai.value.report, ai.value.previous);
    return { status: 'ready', value: buildCostCard('ai', streams, costFx(geo.status === 'ready' ? geo.value : undefined, ai.value.report, streams.map((s) => s.currency))) };
  }, [geo, ai]);

  if (!mayViewMaps && !mayViewAi) return null;
  const priceBookUnset = geo.status === 'ready' && geo.value.priceBook.reasonCode === 'GEO_PRICE_BOOK_UNSET' && geo.value.vendors.length === 0;
  return (
    <Stack gap={4}>
      <Text as="p" role="caption" tone="secondary">{t('dash.block.costs.hint')}</Text>
      <Grid min="lg" gap={4}>
        {mayViewMaps && (
          <CostCard
            title={t('pcost.maps.title')}
            subtitle={t('pcost.maps.subtitle')}
            state={mapsCard}
            what={t('pcost.maps.what')}
            emptyText={priceBookUnset ? t('pcost.empty.maps.unset') : t('pcost.empty.maps')}
            detailsLabel={t('pcost.details.maps')}
            onDetails={() => navigate('maps_spend')}
            originalLabel={(source) => v('geo.vendor', source)}
          />
        )}
        {mayViewAi && (
          <CostCard
            title={t('pcost.ai.title')}
            subtitle={t('pcost.ai.subtitle')}
            state={aiCard}
            what={t('pcost.ai.what')}
            emptyText={t('pcost.empty.ai')}
            detailsLabel={t('pcost.details.ai')}
            onDetails={() => navigate('ai_spend')}
            originalLabel={() => t('pcost.original.ai')}
          />
        )}
      </Grid>
    </Stack>
  );
}
